// ─── extractRouteHighlights ────────────────────────────────────────────
//
// Extrai pontos-chave da rota para o resumo visual ("boarding pass").
// Não precisa de reverse geocoding extra — usa o que já temos:
//   · route.origin / route.destination / route.waypoints
//   · route.routeSteps (com country, distance, label)
//   · route.computedRoute.countryDistances (para identificar transições)
//
// Output: array ordenado por km acumulado, com:
//   { kind, name, country, distKm, durationMin, lat, lng }
//
// kind = 'origin' | 'waypoint' | 'border' | 'city' | 'crossing' | 'destination'
//
// Auto-adaptativo ao tamanho da rota:
//   < 300km:    4-6 pontos
//   300-1500km: 8-12 pontos
//   > 1500km:   15-20 pontos

const ISO3_TO_ISO2 = {
  PRT: 'PT', ESP: 'ES', FRA: 'FR', DEU: 'DE', ITA: 'IT', BEL: 'BE',
  NLD: 'NL', LUX: 'LU', AUT: 'AT', IRL: 'IE', GBR: 'GB', CHE: 'CH',
  DNK: 'DK', SWE: 'SE', NOR: 'NO', FIN: 'FI', POL: 'PL', CZE: 'CZ',
  AGO: 'AO', NAM: 'NA', ZAF: 'ZA', MOZ: 'MZ',
};

// Um código de país válido é ISO-3166 alpha-2 ou alpha-3 (só letras).
// Os routeSteps por vezes trazem lixo no campo `country` (ex.: um UUID
// "de867fef-..."), que fazia countryFlag interpretar "de" → 🇩🇪 (bandeira
// alemã em cidades angolanas). Rejeitamos tudo o que não seja 2-3 letras.
function validCountry(code) {
  if (!code || typeof code !== 'string') return '';
  const raw = code.trim().toUpperCase();
  return /^[A-Z]{2,3}$/.test(raw) ? raw : '';
}

function flagFromIso(code) {
  const raw = validCountry(code);
  if (!raw) return '🌍';
  const iso2 = ISO3_TO_ISO2[raw] || raw.slice(0, 2);
  if (iso2.length !== 2) return '🌍';
  return String.fromCodePoint(...iso2.split('').map(c => 0x1F1E6 + c.charCodeAt(0) - 65));
}

const COUNTRY_NAMES = {
  PT: 'Portugal', ES: 'Espanha', FR: 'França', DE: 'Alemanha', IT: 'Itália',
  BE: 'Bélgica', NL: 'Países Baixos', LU: 'Luxemburgo', AT: 'Áustria',
  IE: 'Irlanda', GB: 'Reino Unido', CH: 'Suíça',
  AO: 'Angola', NA: 'Namíbia', ZA: 'África do Sul', MZ: 'Moçambique',
  PRT: 'Portugal', ESP: 'Espanha', FRA: 'França', DEU: 'Alemanha',
  AGO: 'Angola', GBR: 'Reino Unido', IRL: 'Irlanda',
};

export function countryName(code) {
  const raw = validCountry(code);
  if (!raw) return '';
  return COUNTRY_NAMES[raw] || raw;
}
export function countryFlag(code) { return flagFromIso(code); }

function targetPointCount(totalKm) {
  if (totalKm < 300) return 5;
  if (totalKm < 800) return 8;
  if (totalKm < 1500) return 12;
  if (totalKm < 2500) return 16;
  return 20;
}

export function extractRouteHighlights(route, crossings = [], opts = {}) {
  const cr = route.computedRoute;
  if (!cr || !route.origin?.coordinates || !route.destination?.coordinates) return [];

  const totalDistM = cr.distance || 0;
  const totalKm = totalDistM / 1000;
  const targetN = targetPointCount(totalKm);

  // País por defeito vem das Preferências (clientSettings.defaultCountry) — é a
  // fonte autoritária de país/moeda no planner.
  const prefsCountry = validCountry(opts.defaultCountry);
  // Fonte geográfica: countryDistances (as chaves são códigos ISO válidos, ao
  // contrário do campo `country` dos routeSteps que às vezes traz lixo). Numa
  // rota de país único usamos esse país; caso contrário caímos nas Preferências.
  const cdKeys = Object.keys(cr.countryDistances || {}).map(validCountry).filter(Boolean);
  const soleCountry = (new Set(cdKeys).size === 1 ? cdKeys[0] : '') || prefsCountry;

  const highlights = [];
  const [origLng, origLat] = route.origin.coordinates;
  const [destLng, destLat] = route.destination.coordinates;

  highlights.push({
    kind: 'origin',
    name: route.origin.name || 'Origem',
    country: validCountry(cr.routeSteps?.[0]?.country) || soleCountry,
    distKm: 0,
    durationMin: 0,
    lat: origLat, lng: origLng,
  });

  // Waypoints intermédios — sempre incluídos
  (route.waypoints || []).forEach((wp, i) => {
    if (!wp.coordinates) return;
    const [lng, lat] = wp.coordinates;
    highlights.push({
      kind: wp.waypointKind === 'service' ? 'service'
        : wp.waypointKind === 'pickup' ? 'pickup'
        : 'waypoint',
      name: wp.name || `Paragem ${i + 1}`,
      country: '',
      distKm: null, // recalc abaixo
      durationMin: null,
      lat, lng,
    });
  });

  // Extrair cidades de routeSteps. Cada step tem distance (m), country,
  // label (nome estrada ou cidade). Acumular distância e amostrar cada
  // X km para atingir targetN.
  const steps = cr.routeSteps || [];
  if (steps.length > 0) {
    const stepIntervalKm = totalKm / Math.max(1, targetN - 2);
    let accM = 0;
    let lastSampleKm = 0;
    let lastCountry = '';
    for (let i = 0; i < steps.length; i++) {
      const s = steps[i];
      accM += s.distance || 0;
      const accKm = accM / 1000;
      const stepCountry = validCountry(s.country) || lastCountry;

      // Mudança de país → sempre incluir
      if (stepCountry && stepCountry !== lastCountry && lastCountry !== '') {
        highlights.push({
          kind: 'border',
          name: `Entrada em ${countryName(stepCountry)}`,
          country: stepCountry,
          distKm: accKm,
          durationMin: Math.round((accM / totalDistM) * (cr.duration || 0) / 60),
          lat: null, lng: null,
        });
      }
      lastCountry = stepCountry;

      // Cidades a cada stepIntervalKm
      if (accKm - lastSampleKm >= stepIntervalKm * 0.85 && i < steps.length - 1) {
        const label = (s.name || s.ref || s.label || '').trim();
        // Só interessa se tem nome substancial (ignorar 'A1', 'IP4' isolados)
        const isMeaningful = label.length > 3 && !/^[A-Z]{1,3}[-\s]?\d+$/.test(label);
        if (isMeaningful) {
          highlights.push({
            kind: 'city',
            name: label,
            country: stepCountry || soleCountry,
            distKm: accKm,
            durationMin: Math.round((accM / totalDistM) * (cr.duration || 0) / 60),
            lat: null, lng: null,
          });
          lastSampleKm = accKm;
        }
      }
    }
  }

  // Inserir travessias (crossings do tollData) nas posições correctas.
  // crossings têm distance (metros do troço) mas não temos km acumulado
  // exacto — usar lat/lng da section (departure) se disponível, ou
  // colocar no fim do GBR. MVP: colocar antes da chegada se tiverem
  // destinos UK/IE.
  (crossings || []).forEach(c => {
    highlights.push({
      kind: 'crossing',
      name: c.name || 'Travessia',
      country: c.countryCode || '',
      distKm: null,
      durationMin: null,
      lat: null, lng: null,
      cost: c.cost,
      currency: c.currency || 'EUR',
    });
  });

  highlights.push({
    kind: 'destination',
    name: route.destination.name || 'Destino',
    country: validCountry(lastCountryFromSteps(steps)) || soleCountry,
    distKm: totalKm,
    durationMin: Math.round((cr.duration || 0) / 60),
    lat: destLat, lng: destLng,
  });

  return highlights;
}

function lastCountryFromSteps(steps) {
  if (!steps || steps.length === 0) return '';
  for (let i = steps.length - 1; i >= 0; i--) {
    if (steps[i].country) return steps[i].country;
  }
  return '';
}
